import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mountain, MapPin, HandHelping } from "lucide-react";
import axios from "axios";

import { SEO } from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [volunteers, setVolunteers] = useState([]);

  useEffect(() => {
    fetchVolunteers();
  }, []);

  const fetchVolunteers = async () => {
    try {
      const res = await axios.get(`${API}/volunteers/approved`);
      setVolunteers(res.data);
    } catch (e) {
      console.error("Error fetching volunteers:", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO 
        title="About EK Valleys"
        description="I didn't come to the East Kootenays because of a business plan. I came because of a girl. EK Valleys exists to help answer the question every community asks: What do we do now?"
        keywords="East Kootenay, about ekvalleys, EK Valleys, local community, regional directory"
      />
      <NavbarLight />

      {/* Hero */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Mountain className="w-16 h-16 text-[#2C4A3B] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1915] mb-6 italic">
            About EK Valleys
          </h1>
        </div>
      </section>

      {/* The Arrival */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F7F6F2]" data-testid="about-story-section">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg leading-relaxed text-[#5C5A52]">
            <p className="text-2xl text-[#1A1915] font-heading">
              I didn't come to the East Kootenays because of a business plan.
            </p>
            <p>
              I came because of a girl.
            </p>
            <p>
              Like many people who arrive in this valley, life simply led me here. What I found was something special: strong communities, hardworking people, and a place where relationships still matter. But I also noticed something else — a question that kept appearing everywhere.
            </p>
            <p>
              At coffee shops.<br />
              At job sites.<br />
              Between friends trying to make plans.
            </p>
            <p className="text-[#1A1915] font-medium text-xl italic">
              "What do we do now?"
            </p>
            <p>
              Sometimes it meant where should we eat tonight.<br />
              Sometimes it meant who can fix the roof.<br />
              Sometimes it meant how do we raise money for the school.<br />
              Sometimes it meant where can we find work.
            </p>
            <p>
              And sometimes it meant something simpler.
            </p>
          </div>
        </div>
      </section>

      {/* The Visitor Problem */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg leading-relaxed text-[#5C5A52]">
            <p>
              Visitors arrive here every day — drawn by the mountains, the lakes, the quiet pace of life. But when they get here, they often have the same problem. They open their phones and try Google or social media, only to find scattered information, outdated posts, or generic results that miss the character of the place entirely.
            </p>
            <p>
              They're left asking the same question:
            </p>
            <p className="text-[#1A1915] font-medium text-xl italic">
              "What do we do now?"
            </p>
            <p>
              The truth is, the East Kootenays are full of incredible experiences, businesses, trails, events, and people. But unless you already know someone here, those things can be surprisingly hard to discover.
            </p>
          </div>
        </div>
      </section>

      {/* Respect for Place */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F7F6F2]">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg leading-relaxed text-[#5C5A52]">
            <p>
              And there is something else that matters here too.
            </p>
            <p className="text-[#1A1915] font-medium">
              This place isn't a theme park.<br />
              It isn't a free-for-all.
            </p>
            <p>
              The land matters.<br />
              The communities matter.<br />
              Respect for both is simply part of being here.
            </p>
            <p>
              That's true in the East Kootenays, and it's true in every place that people care deeply about. The best experiences always come from understanding the place you're in and the people who call it home.
            </p>
          </div>
        </div>
      </section>

      {/* The Realization */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg leading-relaxed text-[#5C5A52]">
            <p>
              Over time, I realized the region didn't lack energy, talent, or generosity.
            </p>
            <p className="text-[#1A1915] font-medium">
              What it lacked was a system that connected everything together.
            </p>
            <p>
              My background is in construction and heavy industry — environments where coordination determines whether a project succeeds or fails. On a job site, every trade, every machine, and every worker operates within the same framework. When that framework works, massive things get built.
            </p>
            <p>
              I began asking a simple question:
            </p>
            <p className="text-[#1A1915] font-medium italic">
              What if communities had the same kind of coordination system?
            </p>
            <p>
              What if businesses, contractors, volunteers, events, experiences, and everyday life could exist within a shared regional framework?
            </p>
            <p>
              What if the answer to "What do we do now?" could be found instantly — whether you're planning a date night, looking for a contractor, organizing a fundraiser, exploring the valley for the first time, or starting something new?
            </p>
          </div>
        </div>
      </section>

      {/* What EK Valleys Is */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#1A2F23]" data-testid="about-bcnu-meaning">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#D4A017] mb-8 italic">
            That idea eventually became EK Valleys.
          </h2>
          <div className="space-y-6 text-lg leading-relaxed text-white/80">
            <p>
              EK Valleys isn't just a website or a directory.
            </p>
            <p className="text-white font-medium">
              It's an attempt to build a living map of regional life — one that helps people discover what's here, connect with each other, and participate in the communities they visit or call home.
            </p>
            <p>
              EKValleys.com is the first place we're putting that idea into practice.
            </p>
            <p className="text-[#D4A017] font-medium">
              If it works here, it can work anywhere.
            </p>
          </div>
        </div>
      </section>

      {/* The Universal Question */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F7F6F2]">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg leading-relaxed text-[#5C5A52]">
            <p>
              Because no matter where you go — whether you live there, just arrived, or are visiting for the first time — the question is always the same:
            </p>
            <p className="text-[#1A1915] font-heading text-2xl italic">
              What do we do now?
            </p>
            <p className="text-[#1A1915] font-medium">
              EK Valleys exists to help answer it — while respecting the places and people that make every region worth discovering.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#1A2F23]" data-testid="about-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#D4A017] mb-6 italic">
            Be part of this from the beginning.
          </h2>
          <p className="text-white/80 mb-2">
            Limited founding spots — locked in at 20% off yearly registration price for life. Guaranteed.
          </p>
          <p className="text-white/50 text-sm mb-8">
            Registration rates may change. Yours never will.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mb-8 text-left">
            <div className="bg-[#14B8A6]/10 border border-[#14B8A6]/30 rounded-xl p-4">
              <p className="text-[#14B8A6] font-bold text-lg">Business</p>
              <p className="text-white/80 text-sm">20 spots &middot; $49/yr or 80% equivalent</p>
            </div>
            <div className="bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-xl p-4">
              <p className="text-[#6366F1] font-bold text-lg">Corporate</p>
              <p className="text-white/80 text-sm">10 spots &middot; From $199/yr or 80% equivalent</p>
            </div>
            <div className="bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-xl p-4">
              <p className="text-[#DC2626] font-bold text-lg">Industrial</p>
              <p className="text-white/80 text-sm">2 spots &middot; From $1,599/yr or 80% equivalent</p>
            </div>
          </div>
          <Link
            to="/list-business"
            className="btn-gold inline-flex items-center gap-2 font-semibold px-8 py-4 rounded-lg"
            data-testid="about-cta-btn"
          >
            Claim Your Founding Spot
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Meet Our Volunteers */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F7F6F2]" data-testid="meet-volunteers-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#D4A017] flex items-center justify-center">
                <HandHelping className="w-7 h-7 text-white" />
              </div>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A1915] mb-3">
              Meet Our Volunteers
            </h2>
            <p className="text-[#5C5A52] max-w-xl mx-auto">
              The people who make this community happen. Every hand counts.
            </p>
          </div>

          {volunteers.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {volunteers.map((vol) => (
                <div
                  key={vol.id}
                  className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5 hover:border-[#D4A017]/40 transition-colors"
                  data-testid={`volunteer-card-${vol.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      vol.badge_level === "gold" 
                        ? "bg-gradient-to-br from-[#D4A017] to-[#B8860B]" 
                        : "bg-gradient-to-br from-[#C0C0C0] to-[#A8A8A8]"
                    }`}>
                      <HandHelping className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A1915]">{vol.first_name} {vol.last_name}</h3>
                      <p className="text-xs text-[#5C5A52] flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {vol.town}
                      </p>
                    </div>
                    <span className={`ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      vol.badge_level === "gold"
                        ? "bg-[#D4A017]/15 text-[#B8860B]"
                        : "bg-[#C0C0C0]/20 text-[#808080]"
                    }`}>
                      {vol.badge_level}
                    </span>
                  </div>

                  {vol.bio && (
                    <p className="text-sm text-[#5C5A52] mb-3 line-clamp-2">{vol.bio}</p>
                  )}

                  <div className="flex flex-wrap gap-1">
                    {(vol.talents || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] bg-[#2C4A3B]/8 text-[#2C4A3B] px-2 py-0.5 rounded-full">
                        {t}
                      </span>
                    ))}
                    {(vol.talents || []).length > 3 && (
                      <span className="text-[10px] text-[#5C5A52]">+{vol.talents.length - 3} more</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[#5C5A52] mb-4">
                Be the first to join our volunteer team!
              </p>
              <Link to="/volunteer">
                <Button className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8" data-testid="join-volunteer-btn">
                  <HandHelping className="w-4 h-4 mr-2" />
                  Volunteer Now
                </Button>
              </Link>
            </div>
          )}

          {volunteers.length > 0 && (
            <div className="text-center mt-8">
              <Link to="/volunteer">
                <Button variant="outline" className="font-semibold px-8 border-[#2C4A3B] text-[#2C4A3B]" data-testid="join-volunteer-btn">
                  <HandHelping className="w-4 h-4 mr-2" />
                  Join the Team
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Signoff */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-heading text-2xl text-[#1A1915] italic mb-2">
            — Mason
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
